import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { observeAndUpgradeCharts } from '../../../../utils/echarts-upgrader';
import WebchatWidget from '../../components/WebchatWidget';
import PortalSidebar from './portalSidebar/PortalSidebar';
import type { PortalSidebarUser } from './portalSidebar/PortalSidebar';
import { usePortalStore } from '../../store/portalStore';
import { useAuthStore } from '../../../../store/authStore';
import {onLogoutBroadcast} from '../../../../lib/authChannel';
import { UserRole } from '../../../../enums/userRole';
import { portalApi } from './portalApi';
import { store as widgetStore } from '../../../../widget/state/store';
import type { ChatMessage, FileAttachment, LlmMessage } from '../../../../widget/types';
import { t } from '../../../../widget/i18n';
import { truncateTitle, mapMessages, buildErrorBubble, withPersistedErrorBubble } from './Portal.helpers';
import css from './Portal.module.css';
import { PanelLeft } from 'lucide-react';

export default function Portal() {
  const { widgetId } = useParams<{ widgetId: string }>();
  const widgetRef = useRef<HTMLElement>(null);
  const user = useAuthStore(state => state.user);
  const isSuperAdmin = user?.role?.id === UserRole.SuperAdmin;
  const [widgetReady, setWidgetReady] = useState(false);
  const [portalUserEmail, setPortalUserEmail] = useState<string | null>(null);
  const [portalTitle, setPortalTitle] = useState('');
  const [portalDir, setPortalDir] = useState<'ltr' | 'rtl'>('rtl');
  const [isDrivingMode, setIsDrivingMode] = useState(false);
  
  const {
    sidebarOpen,
    activeConversationId,
    pendingTurns,
    setActiveConversation,
    fetchConversations,
    newChat,
    addPendingTurn,
    resolvePendingTurn,
    markPendingTurnFailed,
    dismissPendingTurn,
    reset: resetPortalState,
    toggleSidebar,
    setSidebarOpen,
  } = usePortalStore();

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Subscribe to widget store for driving mode state
  useEffect(() => {
    return widgetStore.subscribe(() => {
      const active = widgetStore.getState().drivingMode.active;
      setIsDrivingMode(prev => prev !== active ? active : prev);
    });
  }, []);

  // Upgrade SVG charts to interactive ECharts
  useEffect(() => {
    const widget = widgetRef.current;
    if (!widget?.shadowRoot) return;
    return observeAndUpgradeCharts(widget.shadowRoot);
  }, []);

  // Poll for widget readiness as a fallback in case auth completed before the portal
  // attached its event listeners. Cookie-restored sessions may be authenticated
  // without a client-side token, so check auth state instead of token presence.
  useEffect(() => {
    if (widgetReady) return;
    const timer = setInterval(() => {
      const el = widgetRef.current as any;
      const authenticated = el?.isAuthenticated?.();
      if (authenticated) {
        setWidgetReady(true);
        setPortalUserEmail(el?.getUserEmail?.() || null);
        clearInterval(timer);
      }
    }, 500);
    return () => clearInterval(timer);
  }, [widgetReady]);

  // Fetch conversation history only for authenticated (non-anonymous) users.
  // Anonymous users don't persist history across sessions.
  useEffect(() => {
    if (widgetReady && user?.email) {
      fetchConversations();
    }
  }, [widgetReady, user?.email, fetchConversations]);

  // Read widget title + text direction once ready (may be fetched asynchronously
  // from server config, since the widget's language isn't known until then).
  useEffect(() => {
    if (!widgetReady) return;
    const readTitle = () => {
      const el = widgetRef.current as any;
      setPortalDir(el?.getDirection?.() || 'rtl');
      const title = el?.getWidgetTitle?.() || '';
      if (title) {
        setPortalTitle(title);
        return true;
      }
      return false;
    };
    if (readTitle()) return;
    // Title may load after health check - poll briefly
    const timer = setInterval(() => {
      if (readTitle()) clearInterval(timer);
    }, 500);
    const timeout = setTimeout(() => clearInterval(timer), 10000);
    return () => { clearInterval(timer); clearTimeout(timeout); };
  }, [widgetReady]);

  const fetchIterationUsage = useCallback(async (conversationId: string) => {
    if (!isSuperAdmin) return undefined;
    try {
      return await portalApi.getIterationUsage(conversationId);
    } catch (err) {
      console.warn('[portal] Failed to load iteration usage:', err);
      return undefined;
    }
  }, [isSuperAdmin]);

  // Resend a failed/timed-out turn's original message as a brand-new turn.
  // For an already-persisted conversation, the real history must be reloaded
  // first - loading empty arrays here would wipe the client's historyMessages,
  // and the resend would then overwrite the server's saved conversation with
  // just this one retried turn (see webchat-conversation.service.ts's
  // saveConversation, which replaces existing.messages wholesale).
  const handleRetryPendingTurn = useCallback(async (turnId: string) => {
    const turn = pendingTurns[turnId];
    if (!turn) return;
    dismissPendingTurn(turnId);
    const el = widgetRef.current as any;
    // Snapshot which conversation was on screen when Retry was clicked. The
    // reload below is async - if the user navigates to a different conversation
    // before it resolves, applying it anyway would clobber whatever they're now
    // looking at, or resend the retried message into the wrong conversation's
    // context. Abort instead.
    const conversationIdAtRetry = usePortalStore.getState().activeConversationId;
    const staleNavigation = () => usePortalStore.getState().activeConversationId !== conversationIdAtRetry;

    if (turn.conversationId) {
      try {
        const conv = await portalApi.getConversation(turn.conversationId);
        const iterationUsage = await fetchIterationUsage(turn.conversationId);
        if (staleNavigation()) {
          console.warn('[portal] Active conversation changed while reloading for retry - aborting to avoid clobbering the current view');
          return;
        }
        const baseTime = new Date(conv.createdAt).getTime();
        const { messages, toolCalls, historyMessages } = mapMessages(conv.messages, baseTime, iterationUsage);
        el?.loadConversation?.(turn.conversationId, messages, toolCalls, historyMessages);
      } catch (err) {
        console.warn('[portal] Failed to load conversation before retry:', err);
        if (staleNavigation()) return;
        // Fall back to a fresh conversation rather than resending against a
        // stale/unknown history under the real conversation's id.
        el?.resetConversation?.();
      }
    } else {
      el?.resetConversation?.();
    }
    el?.getOrchestrator?.()?.sendMessage?.(turn.message, turn.attachments ?? []);
  }, [pendingTurns, dismissPendingTurn, fetchIterationUsage]);

  // Listen for widget auth events
  const handleWidgetEvent = useCallback((type: string, detail: unknown) => {
    if (type === 'mcp-chat:authenticated') {
      const el = widgetRef.current as any;
      setWidgetReady(true);
      setPortalUserEmail(el?.getUserEmail?.() || null);
    }
    if (type === 'mcp-chat:unauthenticated') {
      const el = widgetRef.current as any;
      setWidgetReady(false);
      setPortalUserEmail(null);
      resetPortalState();
      el?.resetConversation?.();
    }
    if (type === 'mcp-chat:message-sent' && user?.email) {
      const d = detail as {
        turnId?: string;
        message?: string;
        sourceConversationId?: string | null;
        assistantId?: string;
        userMessageId?: string;
        attachments?: FileAttachment[];
      } | null;
      if (!d?.turnId || !d.assistantId) return;
      // Raw text exactly as submitted (may be empty for an attachment-only send) -
      // never substitute the sidebar's placeholder fallback into this. The
      // placeholder is a display-only stand-in for the title, nothing else.
      const rawMessage = d.message ?? '';
      const title = truncateTitle(rawMessage.trim() || t('portalNewChat'));
      addPendingTurn(d.turnId, {
        conversationId: d.sourceConversationId ?? null,
        title,
        message: rawMessage,
        userMessageId: d.userMessageId,
        attachments: d.attachments,
        assistantId: d.assistantId,
      });
      if (d.sourceConversationId === null || d.sourceConversationId === undefined) {
        // Brand-new, not-yet-persisted conversation: the turnId stands in for its identity.
        setActiveConversation(d.turnId);
      }
    }
    if (type === 'mcp-chat:message-received' && user?.email) {
      const d = detail as { turnId?: string; conversationId?: string } | null;
      const turnId = d?.turnId;
      // The conversation is persisted via a fire-and-forget write on the server
      // (see chat-orchestrator.service.ts's saveConversation calls) - it may not
      // be readable yet the instant this event arrives. Keep the pending row
      // showing (don't resolve it) until after a short buffer, so the sidebar
      // never has a gap between the placeholder disappearing and the real row
      // appearing.
      setTimeout(() => {
        if (turnId) resolvePendingTurn(turnId);
        // Carry the active highlight over to the now-persisted conversation -
        // only if the user hasn't since navigated to a different conversation.
        if (turnId && d.conversationId && usePortalStore.getState().activeConversationId === turnId) {
          setActiveConversation(d.conversationId);
        }
        fetchConversations();
      }, 400);
    }
    if (type === 'mcp-chat:error') {
      const d = detail as { turnId?: string } | null;
      if (d?.turnId) markPendingTurnFailed(d.turnId, 'error');
    }
    if (type === 'mcp-chat:turn-timeout') {
      const d = detail as { turnId?: string } | null;
      if (d?.turnId) markPendingTurnFailed(d.turnId, 'timeout');
    }
    if (type === 'mcp-chat:turn-retry') {
      const d = detail as { turnId?: string } | null;
      if (d?.turnId) handleRetryPendingTurn(d.turnId);
    }
    if (type === 'mcp-chat:turn-dismiss') {
      const d = detail as { turnId?: string } | null;
      if (d?.turnId) dismissPendingTurn(d.turnId);
    }
  }, [user?.email, setActiveConversation, fetchConversations, addPendingTurn, resolvePendingTurn, markPendingTurnFailed, resetPortalState, handleRetryPendingTurn, dismissPendingTurn]);

  // Handle selecting a conversation from sidebar
  const handleSelectConversation = useCallback(async (turnOrConvId: string) => {
    const pending = pendingTurns[turnOrConvId];

    if (pending) {
      const effectiveId = pending.conversationId ?? turnOrConvId;
      if (effectiveId === activeConversationId) return;

      if (pending.status === 'sending') {
        // Still in flight: load the real history (or the not-yet-persisted user
        // message) plus a live placeholder bubble, then rebind boundTurnId so
        // the turn's own writes start landing directly in this view again -
        // same mechanism as the normal "stay and watch" case.
        const el = widgetRef.current as any;
        const assistantPlaceholder: ChatMessage = {
          id: pending.assistantId,
          role: 'assistant',
          content: '',
          isStreaming: true,
          timestamp: Date.now(),
        };
        if (pending.conversationId) {
          try {
            const conv = await portalApi.getConversation(pending.conversationId);
            const baseTime = new Date(conv.createdAt).getTime();
            const iterationUsage = await fetchIterationUsage(pending.conversationId);
            const { messages, toolCalls, historyMessages } = mapMessages(conv.messages, baseTime, iterationUsage);
            el?.loadConversation?.(pending.conversationId, [...messages, assistantPlaceholder], toolCalls, historyMessages);
          } catch (err) {
            console.warn('[portal] Failed to load conversation:', err);
          }
        } else {
          // Reuse the real, already-submitted message (id + raw text + attachments) -
          // never synthesize new content here, so the user can never see a bubble
          // (or send the LLM a history entry) that they didn't actually submit.
          const userMessage: ChatMessage = {
            id: pending.userMessageId ?? `${turnOrConvId}-user`,
            role: 'user',
            content: pending.message,
            timestamp: Date.now(),
            attachments: pending.attachments && pending.attachments.length > 0 ? pending.attachments : undefined,
          };
          const historyMessages: LlmMessage[] = [{ role: 'user', content: pending.message }];
          el?.loadConversation?.(turnOrConvId, [userMessage, assistantPlaceholder], [], historyMessages);
        }
        widgetStore.setState({ boundTurnId: turnOrConvId });
      } else {
        // error/timeout: no live turn to rebind to - reconstruct the same history
        // plus a plain error bubble (with Retry/Dismiss) instead of a streaming placeholder.
        const el = widgetRef.current as any;
        const errorBubble = buildErrorBubble(pending.status as 'error' | 'timeout', turnOrConvId);
        if (pending.conversationId) {
          try { 
            const conv = await portalApi.getConversation(pending.conversationId);
            const baseTime = new Date(conv.createdAt).getTime();
            const iterationUsage = await fetchIterationUsage(pending.conversationId);
            const { messages, toolCalls, historyMessages } = mapMessages(conv.messages, baseTime, iterationUsage);
            el?.loadConversation?.(pending.conversationId, [...messages, errorBubble], toolCalls, historyMessages);
          } catch (err) {
            console.warn('[portal] Failed to load conversation:', err);
          }
        } else {
          const userMessage: ChatMessage = {
            id: pending.userMessageId ?? `${turnOrConvId}-user`,
            role: 'user',
            content: pending.message,
            timestamp: Date.now(),
            attachments: pending.attachments && pending.attachments.length > 0 ? pending.attachments : undefined,
          };
          const historyMessages: LlmMessage[] = [{ role: 'user', content: pending.message }];
          el?.loadConversation?.(turnOrConvId, [userMessage, errorBubble], [], historyMessages);
        }
      }

      setActiveConversation(effectiveId);
      if (window.innerWidth <= 768) setSidebarOpen(false);
      return;
    }

    if (turnOrConvId === activeConversationId) return;

    try {
      const conv = await portalApi.getConversation(turnOrConvId);
      const el = widgetRef.current as any;
      if (el?.loadConversation) {
        const baseTime = new Date(conv.createdAt).getTime();
        const iterationUsage = await fetchIterationUsage(turnOrConvId);
        const { messages, toolCalls, historyMessages } = mapMessages(conv.messages, baseTime, iterationUsage);
        el.loadConversation(turnOrConvId, withPersistedErrorBubble(messages, conv), toolCalls, historyMessages);
        setActiveConversation(turnOrConvId);
      }
    } catch (err) {
      console.warn('[portal] Failed to load conversation:', err);
    }

    // On mobile, close sidebar after selection
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  }, [activeConversationId, pendingTurns, setActiveConversation, setSidebarOpen, fetchIterationUsage]);

  // Handle new chat
  const handleNewChat = useCallback(() => {
    const el = widgetRef.current as any;
    el?.resetConversation?.();
    newChat();

    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  }, [newChat, setSidebarOpen]);

  const resetToGuest = useCallback(() => {
    setWidgetReady(false);
    setPortalUserEmail(null);
    resetPortalState();
    (widgetRef.current as any)?.resetConversation?.();
  }, [resetPortalState]);

  useEffect(() => {
    return onLogoutBroadcast(async () => {
      const el = widgetRef.current as any;
      await el?.logout?.();
      resetToGuest();
    });
  }, [resetToGuest]);

  const logout = useAuthStore(state => state.logout);
  const sidebarUser: PortalSidebarUser | null = widgetReady && portalUserEmail
    ? {
        email: portalUserEmail,
        displayName: user?.email === portalUserEmail
          ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || portalUserEmail
          : portalUserEmail,
      }
    : null;

  const handleLogout = useCallback(async () => {
    const el = widgetRef.current as any;
    await el?.logout?.();
    resetToGuest();
    await logout();
  }, [logout, resetToGuest]);

  // No widget id in the URL - there's no admin-configured widget to scope this
  // chat to, so don't mount the widget/sidebar at all (chat would fail on the
  // first message anyway; see ChatOrchestratorService.streamChat).
  if (!widgetId) {
    return (
      <div className={css.unavailable} dir="rtl">
        <div>
          <p className={css.unavailableTitle}>{t('portalInvalidLink')}</p>
          <p className={css.unavailableDesc}>{t('portalInvalidLinkDesc')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={css.portalRoot} dir={portalDir}>
      {/* Sidebar - hidden in driving mode */}
      {sidebarOpen && !isDrivingMode && (
        <>
          <PortalSidebar
            portalTitle={portalTitle}
            user={sidebarUser}
            onSelectConversation={handleSelectConversation}
            onNewChat={handleNewChat}
            onLogout={handleLogout}
          />
          {/* Mobile backdrop */}
          {window.innerWidth <= 768 && (
            <div className={css.mobileBackdrop} onClick={() => setSidebarOpen(false)} />
          )}
        </>
      )}

      {/* Mobile "open menu" button - hidden in driving mode; no equivalent needed
          when the sidebar is already open (backdrop / desktop arrow handle that). */}
      {!sidebarOpen && !isDrivingMode && (
        <div
          onClick={toggleSidebar}
          title={t('portalShowMenu')}
          className={css.sidebarToggle}
        >
          <PanelLeft size={20} color="#6b7280" />
        </div>
      )}

      {/* Main chat area - WebchatWidget always stays mounted so its orchestrator
          and event listeners keep running for backgrounded turns. A failed/timed-out
          turn renders as a plain in-chat message with Retry/Dismiss (see
          orchestrator.ts's turnError handling and handleSelectConversation above),
          not a separate overlay. */}
      <div className={css.mainArea}>
        <WebchatWidget
          ref={widgetRef}
          displayMode="fullscreen"
          portalMode
          serverUrl={import.meta.env.VITE_API_BASE_URL || ''}
          widgetId={widgetId}
          onEvent={handleWidgetEvent}
        />
      </div>

      {/* Sidebar toggle - hidden in driving mode */}
      {!isDrivingMode && <div
        onClick={toggleSidebar}
        title={sidebarOpen ? t('portalHideMenu') : t('portalShowMenu')}
        className={`${css.toggleArrowIcon} ${sidebarOpen? css.toggleArrowIconOpen: css.toggleArrowIconClose}`}
      >
        <svg width="10" height="10" viewBox="0 0 10 10" style={{ display: 'block', transform: portalDir === 'ltr' ? 'scaleX(-1)' : undefined }}>
          {sidebarOpen ? (
            <polyline points="3,1 7,5 3,9" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          ) : (
            <polyline points="7,1 3,5 7,9" fill="none" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          )}
        </svg>
      </div>}
    </div>
  );
}
